import React from 'react'
import { useEffect } from 'react'
import { DomainDto } from '../../../dtos/domain'
import { Well } from '@pushly/aqe/lib/components'
import { getClassNames } from '../../../_utils/classnames'
import { ChannelSwitch } from '../../channel-switch/channel-switch'
import { NotificationIosContent } from './notification-ios-content'
import { NotificationNativeIosContentModel } from '../../../models/notification/notification-native-ios-content.model'
import { NotificationAndroidContent } from './notification-android-content'
import { NotificationNativeAndroidContentModel } from '../../../models/notification/notification-native-android-content.model'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { getEnabledDeliveryChannels } from '../../../_utils/domain'
import { INotificationBuilderState } from '../interfaces/notification-builder-state.interface'
import { useService } from '@pushly/aqe/lib/hooks'
import { DomainService } from '../../../services'
import { ActionDispatcher } from '../types'
import { Collapse, Skeleton } from 'antd'

import './styles/notification-channel-settings.scss'

interface INotificationChannelSettings {
    className?: string
    loading?: boolean
    builder: INotificationBuilderState
    title: string | React.ReactNode
    domain: DomainDto
    disabled?: boolean
    iosValue?: NotificationNativeIosContentModel
    androidValue?: NotificationNativeAndroidContentModel
    dispatchChanges: ActionDispatcher
    onChange: (value: NotificationNativeIosContentModel | NotificationNativeAndroidContentModel) => void
    hideNativeIos?: boolean
    hideNativeAndroid?: boolean
    hideEnableToggle: boolean
}

export const NotificationChannelSettings: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<INotificationChannelSettings>>
> = (props: INotificationChannelSettings) => {
    const {
        className,
        loading,
        title,
        iosValue,
        androidValue,
        hideNativeAndroid,
        hideNativeIos,
        hideEnableToggle,
        domain,
        builder,
        dispatchChanges,
    } = props

    const domainSvc = useService(DomainService)
    const [activeChannels, setActiveChannels] = React.useState<DeliveryChannel[]>(getEnabledDeliveryChannels(domain))

    useEffect(() => {
        const setLoading = (isLoading: boolean) =>
            dispatchChanges({
                type: 'patch',
                entity: 'loading-channel-dependencies',
                data: { channel: DeliveryChannel.NATIVE_ANDROID, loading: isLoading },
            })

        const getNativeAndroidChannels = async () => {
            setLoading(true)

            const { data, ok } = await domainSvc.fetchAllAndroidChannels(domain.id)
            const notificationChannels = ok ? data : []
            dispatchChanges({
                type: 'patch',
                entity: 'channel-dependencies',
                data: { channel: DeliveryChannel.NATIVE_ANDROID, value: { notificationChannels, loading: false } },
            })
        }

        if (
            activeChannels.includes(DeliveryChannel.NATIVE_ANDROID) &&
            !builder.channelDependencies[DeliveryChannel.NATIVE_ANDROID].loading
        ) {
            getNativeAndroidChannels()
        } else {
            setLoading(false)
        }
    }, activeChannels)

    const generateToggle = (channel: DeliveryChannel) => (
        <div onClick={(ev) => ev.stopPropagation()}>
            <ChannelSwitch
                className="selector-embedded"
                value={activeChannels}
                channel={channel}
                onChange={handleChannelChange}
                label="Enabled"
            />
        </div>
    )

    const handleChannelChange = (change: DeliveryChannel[]) => {
        setActiveChannels(change)
    }

    return (
        <Well
            className={getClassNames('notification-channel-settings', className + ' nested')}
            title={title}
            mode="ghost"
            hideHeader={false}
            hideFooter={true}
            flush
        >
            <Skeleton
                loading={loading || builder.channelDependencies[DeliveryChannel.NATIVE_ANDROID].loading}
                active={true}
                avatar={false}
                title={false}
            >
                <Collapse>
                    {!hideNativeIos && (
                        <Collapse.Panel
                            key="native-ios"
                            header="Native: iOS"
                            extra={!hideEnableToggle && generateToggle(DeliveryChannel.NATIVE_IOS)}
                        >
                            <NotificationIosContent
                                domain={domain}
                                value={iosValue!}
                                onChange={(v) => {
                                    const update = v.clone()

                                    props.onChange(update)
                                }}
                            />
                        </Collapse.Panel>
                    )}

                    {!hideNativeAndroid && (
                        <Collapse.Panel
                            key="native-android"
                            header="Native: Android"
                            extra={!hideEnableToggle && generateToggle(DeliveryChannel.NATIVE_ANDROID)}
                        >
                            <NotificationAndroidContent
                                builder={props.builder}
                                value={androidValue}
                                onChange={(v) => {
                                    const update = v.clone()

                                    props.onChange(update)
                                }}
                                domain={domain}
                            />
                        </Collapse.Panel>
                    )}
                </Collapse>
            </Skeleton>
        </Well>
    )
}
